/** Trial balance (M11-03): opening, movement and closing per account. */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, TriangleAlert } from 'lucide-react';
import { api } from '../../lib/api';
import { amount, dec } from '../../lib/money';
import { manilaToday } from '../../lib/queries';
import { Alert, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';

interface Row {
  accountId: string;
  code: string;
  name: string;
  type: string;
  opening: string;
  debit: string;
  credit: string;
  closing: string;
}

interface TrialBalance {
  from: string;
  to: string;
  rows: Row[];
  totals: { debit: string; credit: string; closingDebit: string; closingCredit: string };
  balanced: boolean;
  warnings: string[];
}

const side = (value: string, want: 'dr' | 'cr'): string => {
  const d = dec(value);
  if (want === 'dr') return d.isPositive() ? amount(value) : '';
  return d.isNegative() ? amount(d.abs().toString()) : '';
};

export function TrialBalancePage(): JSX.Element {
  const today = manilaToday();
  const [from, setFrom] = useState(`${today.slice(0, 4)}-01-01`);
  const [to, setTo] = useState(today);

  const tb = useQuery({
    queryKey: ['trial-balance', from, to],
    queryFn: () => api<TrialBalance>(`/reports/trial-balance?from=${from}&to=${to}`),
    enabled: Boolean(from && to && from <= to),
  });

  return (
    <>
      <PageHeader title="Trial Balance" description="Every account with a balance or movement in the range. Voided entries and their reversals both count, and net to zero." />
      <FilterBar>
        <label className="text-sm text-slate-600 dark:text-slate-300">From <input type="date" className={`${filterClass} ml-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label className="text-sm text-slate-600 dark:text-slate-300">To <input type="date" className={`${filterClass} ml-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
        {tb.data && (
          <StatusChip tone={tb.data.balanced ? 'ok' : 'critical'}>
            {tb.data.balanced ? <CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5" /> : <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5" />}
            {tb.data.balanced ? 'Balanced' : 'Out of balance'}
          </StatusChip>
        )}
      </FilterBar>
      {tb.data?.warnings.map((w) => <div key={w} className="mb-4"><Alert tone="warning">{w}</Alert></div>)}
      <Card>
        {tb.isLoading && <Skeleton className="h-96 w-full" />}
        {tb.isError && <Alert tone="critical">Could not run the trial balance.</Alert>}
        {tb.data && tb.data.rows.length === 0 && <EmptyState title="Nothing posted in this range" />}
        {tb.data && tb.data.rows.length > 0 && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Code</Th>
                <Th>Account</Th>
                <Th align="right">Opening</Th>
                <Th align="right">Debit</Th>
                <Th align="right">Credit</Th>
                <Th align="right">Closing Dr</Th>
                <Th align="right">Closing Cr</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {tb.data.rows.map((r) => (
                <tr key={r.accountId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <Td className="tabular-nums">{r.code}</Td>
                  <Td>
                    <Link className="hover:underline" to={`/reports/general-ledger?account=${r.accountId}&from=${from}&to=${to}`}>{r.name}</Link>
                  </Td>
                  <Td align="right" className="text-slate-500"><Money>{dec(r.opening).isZero() ? '' : amount(r.opening)}</Money></Td>
                  <Td align="right"><Money>{amount(r.debit, true)}</Money></Td>
                  <Td align="right"><Money>{amount(r.credit, true)}</Money></Td>
                  <Td align="right"><Money>{side(r.closing, 'dr')}</Money></Td>
                  <Td align="right"><Money>{side(r.closing, 'cr')}</Money></Td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-slate-300 font-bold dark:border-slate-700">
                <Td>{''}</Td>
                <Td>Totals</Td>
                <Td>{''}</Td>
                <Td align="right"><Money>{amount(tb.data.totals.debit)}</Money></Td>
                <Td align="right"><Money>{amount(tb.data.totals.credit)}</Money></Td>
                <Td align="right"><Money>{amount(tb.data.totals.closingDebit)}</Money></Td>
                <Td align="right"><Money>{amount(tb.data.totals.closingCredit)}</Money></Td>
              </tr>
            </tfoot>
          </TableWrap>
        )}
      </Card>
    </>
  );
}
